import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, DualStat } from '../ui/primitives';
import { pct } from '../../lib/format';
import type { EngagementReport } from '../../lib/types';

export interface TimelinePoint {
  day: string;
  opensRaw: number;
  opensFiltered: number;
  clicksRaw: number;
  clicksFiltered: number;
  machineOpens: number;
}

/**
 * Headline engagement. Every figure appears twice - the human-only number we
 * report and the unfiltered number underneath - so the gap is never hidden.
 */
export function EngagementPanel({
  report,
  timeline,
}: {
  report: EngagementReport;
  timeline?: TimelinePoint[];
}) {
  const { counts, rates } = report;

  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <DualStat
          label="Unique opens"
          filtered={report.opens.filtered}
          raw={report.opens.raw}
          excludedPct={report.opens.excludedPct}
        />
        <DualStat
          label="Unique clicks"
          filtered={report.clicks.filtered}
          raw={report.clicks.raw}
          excludedPct={report.clicks.excludedPct}
        />
        <div className="rounded-xl border border-ink-200 bg-white p-4 shadow-sm">
          <div className="text-xs font-medium uppercase tracking-wide text-ink-500">Open rate</div>
          <div className="mt-2 text-2xl font-semibold tabular-nums text-ink-900">
            {pct(rates.openRateFiltered)}
          </div>
          <div className="mt-1.5 text-xs text-ink-500">{pct(rates.openRateRaw)} unfiltered</div>
        </div>
        <div className="rounded-xl border border-ink-200 bg-white p-4 shadow-sm">
          <div className="text-xs font-medium uppercase tracking-wide text-ink-500">
            Click-to-open rate
          </div>
          <div className="mt-2 text-2xl font-semibold tabular-nums text-ink-900">
            {pct(rates.ctorFiltered)}
          </div>
          <div className="mt-1.5 text-xs text-ink-500">{pct(rates.ctorRaw)} unfiltered</div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <SmallStat label="Sent" value={counts.sent.toLocaleString()} />
        <SmallStat label="Delivered" value={counts.delivered.toLocaleString()} hint={pct(rates.deliveryRate)} />
        <SmallStat label="Bounced" value={counts.bounced.toLocaleString()} hint={pct(rates.bounceRate)} />
        <SmallStat
          label="Complaints"
          value={counts.complaints.toLocaleString()}
          hint={pct(rates.complaintRate, 3)}
        />
        <SmallStat
          label="Unsubscribes"
          value={counts.unsubscribes.toLocaleString()}
          hint={pct(rates.unsubscribeRate)}
        />
      </div>

      {timeline && timeline.length > 0 && (
        <Card
          title="Engagement over time"
          subtitle="Solid lines are human-only engagement. Dotted lines include everything we recorded, automated activity included."
        >
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timeline} margin={{ top: 8, right: 16, bottom: 0, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{
                    borderRadius: 8,
                    border: '1px solid #e2e8f0',
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="opensFiltered"
                  name="Opens (human)"
                  stroke="#0f172a"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="opensRaw"
                  name="Opens (unfiltered)"
                  stroke="#94a3b8"
                  strokeDasharray="4 3"
                  strokeWidth={1.5}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="clicksFiltered"
                  name="Clicks (human)"
                  stroke="#0284c7"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="machineOpens"
                  name="Machine opens (Apple MPP etc.)"
                  stroke="#f59e0b"
                  strokeDasharray="2 2"
                  strokeWidth={1.5}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}
    </div>
  );
}

function SmallStat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-ink-200 bg-white px-4 py-3">
      <div className="text-xs text-ink-500">{label}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-lg font-semibold tabular-nums text-ink-900">{value}</span>
        {hint && <span className="text-xs text-ink-500">{hint}</span>}
      </div>
    </div>
  );
}
